/**
 * Shared fetch + React Query key for the public uptime window endpoint
 * (`GET /api/v1/components/:id/uptime?window=&anchor=`). Centralizes what
 * `App.tsx`'s `api.uptime` and `IndexerLagCards`'s `fetchUptime1h` each did
 * separately.
 *
 * Dedup note: the cache key is `['uptime', id, window, anchor]`, matching
 * App's 4-element key — but ONLY when the caller threads the same `anchor`
 * through BOTH `uptimeQueryKey` and `fetchUptimeWindow`. A consumer that
 * passes no anchor (anchor = '') keys/fetches the live window and only
 * dedupes with other live-window readers.
 *
 * Runtime note: the endpoint returns one rich JSON object; each consumer types
 * the slice it needs. The cache stores the full payload regardless of which
 * consumer's `queryFn` ran (they all `response.json()` the same URL), so a
 * lag-focused view here coexists safely with App's full-shape view under the
 * shared key.
 */
import type { LagWindow } from './lag-time';
import type { EndpointSummary } from './status-types';

/**
 * Lag-focused slice of the read-API `EndpointSummary` (the single source of
 * truth in `status-types.ts`). Derived via `Pick` so it can never drift from
 * the full DTO shape.
 */
export type UptimeLagEndpoint = Pick<
  EndpointSummary,
  | 'endpointKey'
  | 'status'
  | 'currentLagSec'
  | 'avgLagSec'
  | 'p95LagSec'
  | 'maxLagSec'
  | 'currentLagBlocks'
  | 'p95LagBlocks'
  | 'maxLagBlocks'
  | 'lagBuckets'
>;

export interface UptimeWindowData {
  window: LagWindow;
  /** Backend-authoritative bucket width (ms); may be absent on old deploys. */
  bucketMs?: number;
  endpoints: UptimeLagEndpoint[];
}

/** Cache key shared with App.tsx (`['uptime', id, window, anchor]`). */
export function uptimeQueryKey(
  id: number,
  window: LagWindow,
  anchor = '',
): readonly [string, number, LagWindow, string] {
  return ['uptime', id, window, anchor];
}

export async function fetchUptimeWindow(
  id: number,
  window: LagWindow,
  anchor = '',
): Promise<UptimeWindowData> {
  const params = new URLSearchParams({ window });
  if (anchor !== '') {
    params.set('anchor', anchor);
  }
  const response = await fetch(
    `/api/v1/components/${String(id)}/uptime?${params.toString()}`,
    { headers: { Accept: 'application/json' } },
  );
  if (!response.ok) {
    throw new Error(
      `uptime fetch failed for component ${String(id)}: ${String(response.status)}`,
    );
  }
  const json: unknown = await response.json();
  if (
    json === null ||
    typeof json !== 'object' ||
    !Array.isArray((json as { endpoints?: unknown }).endpoints)
  ) {
    throw new Error(
      `malformed uptime payload for component ${String(id)}`,
    );
  }
  return json as UptimeWindowData;
}
